import { BadgeDelta, Card, Metric, Text } from "@tremor/react";
import type { CaseCounts, Outbreak } from "#/server/data-fetchers.js";
import { formatNumber, formatPercent, type TranslateFn } from "#/lib/i18n.js";
import { SourceCitation, type Source } from "#/components/shared/SourceCitation.js";

type Props = {
  outbreak: Outbreak;
  outbreaks: Outbreak[];
  caseCounts: CaseCounts;
  locale: string;
  t: TranslateFn;
};

type DeltaType =
  | "increase"
  | "moderateIncrease"
  | "unchanged"
  | "moderateDecrease"
  | "decrease";

function classifyDelta(current: number, prior: number): DeltaType {
  if (prior === 0 && current === 0) return "unchanged";
  if (prior === 0) return current > 0 ? "increase" : "unchanged";
  const ratio = (current - prior) / Math.abs(prior);
  if (ratio > 0.1) return "increase";
  if (ratio > 0.01) return "moderateIncrease";
  if (ratio < -0.1) return "decrease";
  if (ratio < -0.01) return "moderateDecrease";
  return "unchanged";
}

function deltaLabel(
  current: number,
  prior: number,
  locale: string,
  t: TranslateFn,
): string {
  if (prior === 0 && current === 0) return t("kpi.noChange");
  if (prior === 0) {
    return `+${formatNumber(current, locale)} (${t("kpi.delta7d")})`;
  }
  const diff = current - prior;
  const pctValue = (diff / prior) * 100;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${formatPercent(pctValue, locale)} (${t("kpi.delta7d")})`;
}

export function KpiGrid({
  outbreak,
  outbreaks,
  caseCounts,
  locale,
  t,
}: Props) {
  const activeCount = outbreaks.filter(
    (o) => o.status === "active" || o.status === "emerging",
  ).length;
  // Approximation for prior week — when we don't have a snapshot, assume
  // the count was 1 less per emerging outbreak. The real series feeds
  // through `outbreak.list({ asOf: ... })` once #27 wires it.
  const activePrior = Math.max(0, activeCount - 1);

  const totalCases = caseCounts.confirmedTotal;
  const totalCasesPrior = caseCounts.confirmedSevenDaysAgo;

  const populationValues = outbreaks
    .filter((o) => o.status === "active" || o.status === "emerging")
    .map((o) => o.populationAtRisk)
    .filter((value): value is number => typeof value === "number" && value > 0);
  const populationAtRisk =
    populationValues.length > 0
      ? populationValues.reduce((sum, value) => sum + value, 0)
      : null;
  // Population at risk doesn't have a 7-day series; show absolute only.

  const cfr = caseCounts.cfrPercent;
  const cfrPrior = caseCounts.cfrSevenDaysAgo;

  const outbreakSources: Source[] = outbreak.sources ?? [];
  const caseSources: Source[] = caseCounts.sources ?? [];
  const cardStyle: React.CSSProperties = {
    background: "var(--color-bg-surface)",
    border: "1px solid var(--color-border-default)",
    borderRadius: "0.5rem",
    padding: "1.25rem",
  };

  return (
    <section
      aria-label="Key indicators"
      style={{
        paddingBlock: "2rem",
        paddingInline: "1.5rem",
        borderBlockEnd: "1px solid var(--color-border-subtle)",
      }}
    >
      <div
        style={{
          maxWidth: "76rem",
          marginInline: "auto",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1rem",
        }}
      >
        <Card decoration="top" decorationColor="red" style={cardStyle}>
          <Text style={{ color: "var(--color-fg-muted)" }}>
            {t("kpi.activeOutbreaks")}
          </Text>
          <Metric
            style={{
              color: "var(--color-fg-default)",
              fontFamily: "var(--font-mono)",
              marginBlockStart: "0.375rem",
            }}
          >
            <SourceCitation
              sources={outbreakSources}
              locale={locale}
              t={t}
            >
              {formatNumber(activeCount, locale)}
            </SourceCitation>
          </Metric>
          <BadgeDelta
            deltaType={classifyDelta(activeCount, activePrior)}
            isIncreasePositive={false}
            style={{ marginBlockStart: "0.625rem" }}
          >
            {deltaLabel(activeCount, activePrior, locale, t)}
          </BadgeDelta>
        </Card>

        <Card decoration="top" decorationColor="red" style={cardStyle} data-testid="kpi-total-cases">
          <Text style={{ color: "var(--color-fg-muted)" }}>
            {t("kpi.totalCases")}
          </Text>
          <Metric
            style={{
              color: "var(--color-fg-default)",
              fontFamily: "var(--font-mono)",
              marginBlockStart: "0.375rem",
            }}
          >
            <SourceCitation
              sources={caseSources}
              locale={locale}
              t={t}
            >
              {formatNumber(totalCases, locale)}
            </SourceCitation>
          </Metric>
          <BadgeDelta
            deltaType={classifyDelta(totalCases, totalCasesPrior)}
            isIncreasePositive={false}
            style={{ marginBlockStart: "0.625rem" }}
          >
            {deltaLabel(totalCases, totalCasesPrior, locale, t)}
          </BadgeDelta>
        </Card>

        <Card decoration="top" decorationColor="red" style={cardStyle}>
          <Text style={{ color: "var(--color-fg-muted)" }}>
            {t("kpi.populationAtRisk")}
          </Text>
          <Metric
            style={{
              color: "var(--color-fg-default)",
              fontFamily: "var(--font-mono)",
              marginBlockStart: "0.375rem",
            }}
          >
            <SourceCitation
              sources={outbreakSources}
              locale={locale}
              t={t}
            >
              {populationAtRisk === null ? "—" : formatNumber(populationAtRisk, locale)}
            </SourceCitation>
          </Metric>
        </Card>

        <Card decoration="top" decorationColor="red" style={cardStyle}>
          <Text style={{ color: "var(--color-fg-muted)" }}>
            {t("kpi.cfr")}
          </Text>
          <Metric
            style={{
              color: "var(--color-fg-default)",
              fontFamily: "var(--font-mono)",
              marginBlockStart: "0.375rem",
            }}
          >
            <SourceCitation
              sources={caseSources}
              locale={locale}
              t={t}
            >
              {formatPercent(cfr, locale)}
            </SourceCitation>
          </Metric>
          <BadgeDelta
            deltaType={classifyDelta(cfr, cfrPrior)}
            isIncreasePositive={false}
            style={{ marginBlockStart: "0.625rem" }}
          >
            {deltaLabel(cfr, cfrPrior, locale, t)}
          </BadgeDelta>
        </Card>
      </div>
    </section>
  );
}
